import type { Observation } from 'fhir/r4';

import { FHIRProxyClient, FHIRUtils, ProvenanceBuilder } from '../fhir-sdoh';

/**
 * Options for soft-deleting a profile Observation with Provenance audit.
 */
export type SoftDeleteObservationOptions = {
  /** Full Observation resource from the list/search payload. */
  observation: Observation;
  /** FHIR proxy bearer token. */
  token: string;
  /** Patient id the observation belongs to (required for Provenance subject target). */
  patientId: string;
  /** Practitioner id performing the soft delete. */
  practitionerId: string;
  /** Practitioner display name (last-modified-by + Provenance agent display). */
  practitionerName?: string | undefined;
  /**
   * Member program/org code for the Provenance organization agent.
   *
   * `| undefined` rather than a bare `?:`: every profile call site forwards an optional
   * `props.org` positionally, which under `exactOptionalPropertyTypes` is an explicit
   * pass of `undefined` rather than an omission. The destructure defaults it to `'pw'`.
   */
  org?: string | undefined;
  /** Optional reason display for Provenance (defaults from observation code). */
  reason?: string;
};

/**
 * Soft-deletes a FHIR Observation by setting `status` to `entered-in-error` and
 * creates a Provenance resource (activity DELETE) in the same transaction bundle.
 *
 * @param options - Soft-delete payload and actor context.
 * @returns Promise that resolves when the FHIR transaction succeeds.
 */
export async function softDeleteObservation(options: SoftDeleteObservationOptions): Promise<void> {
  const {
    observation,
    token,
    patientId,
    practitionerId,
    practitionerName,
    org = 'pw',
    reason,
  } = options;

  if (!observation?.id) {
    throw new Error('Observation has no id.');
  }
  if (!patientId) {
    throw new Error('Patient id is required to soft-delete an Observation.');
  }
  if (!practitionerId) {
    throw new Error('Practitioner id is required to soft-delete an Observation.');
  }

  let body: Observation = {
    ...observation,
    status: 'entered-in-error',
  };

  if (practitionerName) {
    body = FHIRUtils.addUpdatedBy(body, practitionerName);
  }

  const coding = observation.code?.coding?.[0];
  const codeLabel = coding?.display || coding?.code || observation.id;
  const valueLabel =
    observation.valueString?.trim() ||
    observation.valueCodeableConcept?.coding?.[0]?.display ||
    observation.valueCodeableConcept?.coding?.[0]?.code ||
    (observation.valueQuantity?.value != null
      ? `${observation.valueQuantity.value}${observation.valueQuantity.unit ? ` ${observation.valueQuantity.unit}` : ''}`
      : '') ||
    observation.id;
  const reasonDesc =
    reason ?? `${codeLabel} observation set to entered-in-error: ${valueLabel}`;

  const orgCode = (org || 'pw').toUpperCase();
  const orgDisplay = (org || 'pw').toLowerCase();

  const provenance = ProvenanceBuilder.build(
    `Observation/${observation.id}`,
    {
      reference: `Practitioner/${practitionerId}`,
      display: practitionerName || practitionerId,
    },
    patientId,
    'DELETE',
    reasonDesc,
    { code: orgCode, display: orgDisplay }
  );

  const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([body], provenance);
  const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
  const fhirProxyClient = new FHIRProxyClient(token, proxyURL);

  await fhirProxyClient.send('post', '', fhirBundle);
}
